import React, { useEffect, useRef, useState } from 'react'
import { OmButton, OmDropdownMenu, OmInput, OmDivider, type OmMenuItem } from '@owma/kit'
import classNames from 'classnames'
import { store as reduxStore } from 'index'
import moment from 'moment-timezone'

import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import { format12Hours, format24Hours } from './timeFormats'

export { RegExpTime, TimeParserFor12Hour } from './timeConstants'
import { RegExpTime, TimeParserFor12Hour, get12FormatTime } from './timeConstants'

import styles from './EventEditor.module.scss'

export const parseHourMinute = (
  v: string,
  is12HourFormat: boolean,
): { hour: number; minute: number } => {
  if (is12HourFormat) {
    return {
      hour: TimeParserFor12Hour.getHour(v),
      minute: parseInt(TimeParserFor12Hour.getMinute(v), 10),
    }
  }
  const [hourStr, minuteStr] = v.split(':')
  return { hour: parseInt(hourStr, 10), minute: parseInt(minuteStr, 10) }
}

const ScheduleIcon: React.FC<{ color?: string }> = ({ color = '#000' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 20.5C16.6944 20.5 20.5 16.6944 20.5 12C20.5 7.30558 16.6944 3.5 12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 16.6944 7.30558 20.5 12 20.5Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 7V12.2686H17.0535"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const DurationIcon: React.FC<{ color?: string }> = ({ color = '#000' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 12C18 15.3137 15.3137 18 12 18C8.68629 18 6 15.3137 6 12C6 8.68629 8.68629 6 12 6V12H18Z"
      fill={color}
    />
    <path
      d="M12 20.5C16.6944 20.5 20.5 16.6944 20.5 12C20.5 7.30558 16.6944 3.5 12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 16.6944 7.30558 20.5 12 20.5Z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

interface EditEventData {
  startDate: Date
  endDate: Date
  startTz: string
}

interface CustomTimeSelectorProps {
  timing: 'start' | 'end'
  onTimeChange: (time: string) => void
  editEventData: EditEventData
  defaultValue: string
}

const CustomTimeSelector: React.FC<CustomTimeSelectorProps> = ({
  timing,
  onTimeChange,
  editEventData,
  defaultValue,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [duration, setDuration] = useState(true)
  const [value, setValue] = useState(defaultValue)
  const oldValue = useRef(defaultValue)

  const [isDurationDisabled, setIsDurationDisabled] = useState(false)

  const { settings } = reduxStore.getState()
  const { timeFormat: settingTimeFormat } = settings.general

  const is12HourFormat = settingTimeFormat === 'h:mm A'
  const isTimingEnd = timing === 'end'
  const formattedTime = is12HourFormat ? format12Hours : format24Hours

  const timeList = ({ isEndDate }: { isEndDate: boolean }) => {
    const times: Array<{ label: React.JSX.Element; value: string }> = []
    const startDate = moment(editEventData.startDate).tz(editEventData.startTz)
    const isSameDay =
      isEndDate && moment(editEventData.endDate).isSame(editEventData.startDate, 'day')

    let current = null
    if (isEndDate && isSameDay) {
      current = startDate.clone()
    } else {
      current = startDate.clone().startOf('day').clone()
      const minutes = startDate.minutes()
      if (minutes !== 0 && minutes !== 30) {
        current.add(minutes, 'minutes')
      }
    }
    const endDate = startDate.clone().endOf('day')
    while (current <= endDate) {
      const timeStr = is12HourFormat
        ? format12Hours(current, editEventData.startTz)
        : format24Hours(current, editEventData.startTz)

      if (isSameDay && isEndDate && current.isBefore(startDate)) {
        current.add(30, 'minutes')
        continue
      }

      let durationText = ''
      if (isEndDate && duration) {
        const diffInHours = (current.valueOf() - startDate.valueOf()) / (1000 * 60 * 60)
        durationText =
          diffInHours === 0 || diffInHours === 0.5
            ? `${diffInHours === 0 ? '0' : '30'}m`
            : diffInHours === Math.floor(diffInHours)
              ? `${diffInHours}h`
              : `${Math.floor(diffInHours)}.5h`
      }

      times.push({
        label: (
          <div className={styles.timeItem}>
            <span style={{ color: '#1F2937' }}>{timeStr}</span>
            {isTimingEnd && current.isSame(startDate, 'day') && durationText && (
              <span style={{ color: '#666' }}> ({durationText})</span>
            )}
          </div>
        ),
        value: timeStr,
      })
      current.add(30, 'minutes')
    }

    return times
  }

  // validation check

  const validateTime = (): boolean => {
    const validated = (v: string): boolean => {
      const { hour, minute } = parseHourMinute(v, is12HourFormat)
      return hour <= 23 && hour >= 0 && minute < 60 && minute >= 0
    }

    if (isTimingEnd) {
      if (!validated(value)) return false

      const aStartDate = editEventData.startDate
      const aEndDate = new Date(editEventData.endDate)
      const { hour, minute } = parseHourMinute(value, is12HourFormat)
      aEndDate.setHours(hour, minute, 0, 0)
      return aStartDate <= aEndDate
    } else {
      return validated(value)
    }
  }

  const validateFormatTime = (v: string): boolean => {
    if (!v) return false

    if (is12HourFormat) {
      if (!RegExpTime.get12Hours().test(v)) return false

      const time = get12FormatTime(v)

      const [hourStr, minuteStr] = time.split(':')
      const hour = parseInt(hourStr, 10)
      const minute = parseInt(minuteStr, 10)

      return hour >= 1 && hour <= 12 && minute >= 0 && minute < 60
    } else {
      if (!RegExpTime.get24Hours().test(v)) return false

      const [hourStr, minuteStr] = v.split(':')
      const hour = parseInt(hourStr, 10)
      const minute = parseInt(minuteStr, 10)

      return hour >= 0 && hour <= 23 && minute >= 0 && minute < 60
    }
  }

  // scroll to selected
  const eventTime = timing === 'start' ? editEventData.startDate : editEventData.endDate
  const scrollToSelected = () => {
    if (dropdownRef.current) {
      setTimeout(() => {
        const normalizedValue = formattedTime(eventTime, editEventData.startTz)

        // 이전 선택 항목의 selected 클래스 제거
        const prevSelected = dropdownRef.current!.querySelectorAll(
          '.om-dropdown-menu-item-selected',
        )
        prevSelected.forEach((el) => el.classList.remove('om-dropdown-menu-item-selected'))

        const timeItems = dropdownRef.current!.querySelectorAll(`.${styles.timeItem}`)
        if (timeItems.length > 0) {
          for (const item of Array.from(timeItems)) {
            const normalizedItemText = item.textContent || ''
            if (normalizedItemText.startsWith(normalizedValue)) {
              const parentElement = item.closest('.om-dropdown-menu-item')
              if (parentElement) {
                parentElement.classList.add('om-dropdown-menu-item-selected')
                parentElement.scrollIntoView({ block: 'start', inline: 'nearest' })
                break
              }
            }
          }
        }
      }, 100)
    }
  }

  useEffect(() => {
    if (dropdownOpen) {
      scrollToSelected()
    }
  }, [dropdownOpen])

  useEffect(() => {
    const formattedTime = is12HourFormat
      ? format12Hours(eventTime, editEventData.startTz)
      : format24Hours(eventTime, editEventData.startTz)
    setValue(formattedTime)

    // 시간 변경 시 드롭다운의 이전 선택 표시 제거 (드롭다운 열기 전에 정리)
    if (dropdownRef.current) {
      const prevSelected = dropdownRef.current.querySelectorAll('.om-dropdown-menu-item-selected')
      prevSelected.forEach((el) => el.classList.remove('om-dropdown-menu-item-selected'))
    }

    if (moment(editEventData.startDate).isSame(editEventData.endDate, 'day') === false) {
      setIsDurationDisabled(true)
      setDuration(false)
    } else {
      setIsDurationDisabled(false)
    }
  }, [editEventData])

  const dropRender = (menu?: React.ReactNode): React.ReactNode => {
    const BLUE = '#007AFF'
    const GRAY = '#666666'

    return (
      <div ref={dropdownRef}>
        {menu}
        {isTimingEnd && (
          <React.Fragment>
            <OmDivider style={{ margin: '8px 0' }} />
            <div className={styles.timeDropdownIcons}>
              <OmButton onClick={() => setDuration(true)} disabled={isDurationDisabled}>
                <DurationIcon color={duration ? BLUE : GRAY} />
              </OmButton>
              <OmButton onClick={() => setDuration(false)}>
                <ScheduleIcon color={duration ? GRAY : BLUE} />
              </OmButton>
            </div>
          </React.Fragment>
        )}
      </div>
    )
  }

  const items: OmMenuItem[] = timeList({ isEndDate: isTimingEnd }).map((option) => ({
    key: option.value,
    label: option.label,
  }))

  const handleTimeChange = (time: string) => {
    const formattedTime = is12HourFormat ? TimeParserFor12Hour.getTime(time) : `${time}`
    onTimeChange(formattedTime)
    setValue(time)
  }

  return (
    <div className={classNames(styles.timeSelect, 'customTimeSelector')}>
      <OmInput
        value={value}
        onChange={(next) => {
          setValue(next)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            ;(e.target as HTMLInputElement).blur()
          } else if (e.key === 'Escape') {
            setValue(oldValue.current)
            ;(e.target as HTMLInputElement).value = oldValue.current
            ;(e.target as HTMLInputElement).blur()
          }
        }}
        onBlur={(e) => {
          const newValue = e.target.value

          const isInvalid = !validateFormatTime(newValue) || validateTime() === false
          if (isInvalid) {
            setValue(oldValue.current)
            return
          }

          oldValue.current = newValue
          handleTimeChange(newValue)
        }}
      />
      <OmDropdownMenu
        menu={{ items, onClick: (info) => handleTimeChange(info.key) }}
        placement="bottomRight"
        trigger={['click']}
        open={dropdownOpen}
        onOpenChange={(open) => {
          setDropdownOpen(open)
        }}
        align={{ offset: [0, 10] }}
        overlayClassName={classNames('contextMenu', styles.timeDropdown)}
        popupRender={dropRender}
      >
        <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
      </OmDropdownMenu>
    </div>
  )
}

export default CustomTimeSelector
